/**
 * The scopes `shopify.app.toml` declares, parsed from the ACTUAL file text at
 * build time — never retyped as a JS array.
 *
 * 🔴 WHY A PARSE, NOT A CONSTANT. `shopify.app.toml`'s `[access_scopes]` is the
 * single real authority for what Mora asks Shopify for
 * (`supabase/functions/_shared/shopify-scopes.ts`'s own docblock says exactly
 * this: "This constant must be kept identical to the TOML"). Retyping that list
 * a second time as a TypeScript array is precisely the mistake the toml's own
 * comment warns about — "the scope list was previously hardcoded in three
 * separate places, which had already drifted apart" — so this module imports
 * the toml's raw text with Vite's `?raw` suffix (declared generically for any
 * extension by `vite/client`, referenced from `src/vite-env.d.ts`) and
 * regex-parses the two scope lines out of it. Edit the toml and this list moves
 * with it on the next build — nothing here needs to change.
 *
 * This is the ENTIRE reason `detectShopifyRequiredScopeDrift` (in
 * `shopify-permissions.ts`) can compare "what Mora needs" against "what a store
 * granted" without a live network call: the declared side comes from this
 * module, the granted side comes from the connection's stored
 * `connection_data.scopes` snapshot — both available with zero I/O.
 */
import shopifyAppTomlSource from '../../../shopify.app.toml?raw';

/**
 * `scopes = "a,b,c"` or `optional_scopes = ["a", "b"]` — both shapes appear in
 * the toml today. Anchored to the START of a line so `optional_scopes` can
 * never satisfy a search for `scopes`.
 */
function parseTomlScopeList(source: string, key: 'scopes' | 'optional_scopes'): string[] {
  const lineMatch = source.match(new RegExp(`^${key}\\s*=\\s*(.+)$`, 'm'));
  if (!lineMatch) return [];
  const value = lineMatch[1].trim();
  const inner = value.replace(/^\[/, '').replace(/\]$/, '');
  return inner
    .split(',')
    .map((piece) => piece.trim().replace(/^["']/, '').replace(/["']$/, '').toLowerCase())
    .filter(Boolean);
}

/** `shopify.app.toml`'s `[access_scopes] scopes = "…"` — every shop must grant these at install. */
export const SHOPIFY_DECLARED_REQUIRED_SCOPES: readonly string[] = parseTomlScopeList(
  shopifyAppTomlSource,
  'scopes',
);

/** `shopify.app.toml`'s `[access_scopes] optional_scopes = […]` — requestable dynamically after deployment. */
export const SHOPIFY_DECLARED_OPTIONAL_SCOPES: readonly string[] = parseTomlScopeList(
  shopifyAppTomlSource,
  'optional_scopes',
);

/** Exposed for tests that want to parse a different (e.g. mutated) toml string against the same rule. */
export { parseTomlScopeList };
